import React, { useRef, useState } from "react";
import { formatSize } from "../media";
import { IcoCloudUp, IcoFolderUp, IcoX, IcoInfo, IcoAlert } from "./Icons";

const MAX_LISTED = 60;

// Hộp tải lên dùng chung cho "tải file" và "tải cả thư mục".
// - mode "files":  bấm để chọn nhiều file, hoặc kéo thả file vào khung.
// - mode "folder": bấm để chọn một thư mục (trình duyệt gửi kèm đường dẫn con).
// Việc kiểm tra loại file và gửi lên máy chủ do App đảm nhiệm qua onSubmit.
export default function UploadModal({
  mode = "files", title, note, targetLabel, allowedExt = [], skipNames = [],
  onSubmit, onCancel,
}) {
  const inputRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [dragging, setDragging] = useState(false);

  const isFolder = mode === "folder";
  const isAllowed = (f) => allowedExt.length === 0
    || allowedExt.includes(f.name.split(".").pop().toLowerCase());

  const pick = (list) => {
    // File hệ thống (Thumbs.db, .DS_Store…) lẫn trong thư mục thì bỏ qua luôn.
    const picked = Array.from(list || []).filter((f) => !skipNames.includes(f.name.toLowerCase()));
    if (picked.length === 0) return;
    // Chọn thư mục thì thay cả danh sách; chọn file thì cộng dồn (bỏ file trùng tên + dung lượng).
    setFiles((prev) => {
      if (isFolder) return picked;
      const seen = new Set(prev.map((f) => `${f.name}|${f.size}`));
      return [...prev, ...picked.filter((f) => !seen.has(`${f.name}|${f.size}`))];
    });
  };

  const removeAt = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    if (!isFolder) pick(e.dataTransfer.files);
  };

  const total = files.reduce((sum, f) => sum + (f.size || 0), 0);
  const invalid = files.filter((f) => !isAllowed(f)).length;
  const listed = files.slice(0, MAX_LISTED);

  return (
    <div className="modal" role="dialog" aria-modal="true" aria-label={title}>
      <div className="modal-card modal-wide">
        <div className="modal-head">
          <span className="modal-ico">{isFolder ? <IcoFolderUp size={20} /> : <IcoCloudUp size={20} />}</span>
          <div>
            <h3>{title}</h3>
            <p>Lưu vào: <b>{targetLabel}</b></p>
          </div>
          <button className="icon-btn ghost modal-x" onClick={onCancel} aria-label="Đóng">
            <IcoX size={20} />
          </button>
        </div>

        <div className="modal-body">
          <div
            className={`dropzone ${dragging ? "is-dragging" : ""}`}
            role="button"
            tabIndex={0}
            onClick={() => inputRef.current?.click()}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); inputRef.current?.click(); } }}
            onDragOver={(e) => { e.preventDefault(); if (!isFolder) setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
          >
            <span className="dropzone-ico">{isFolder ? <IcoFolderUp size={26} /> : <IcoCloudUp size={26} />}</span>
            <strong>{isFolder ? "Bấm để chọn thư mục" : "Kéo thả file vào đây, hoặc bấm để chọn"}</strong>
            <span>Ảnh JPG, PNG, GIF, WEBP · video · PDF</span>
            {isFolder ? (
              <input
                ref={inputRef}
                type="file"
                webkitdirectory="true"
                multiple
                hidden
                onChange={(e) => { pick(e.target.files); e.target.value = ""; }}
              />
            ) : (
              <input
                ref={inputRef}
                type="file"
                multiple
                hidden
                onChange={(e) => { pick(e.target.files); e.target.value = ""; }}
              />
            )}
          </div>

          {files.length > 0 && (
            <div className="picked">
              <div className="picked-head">
                <span>{files.length} file · {formatSize(total) || "0 B"}</span>
                <button className="link-btn" onClick={() => setFiles([])}>Bỏ chọn hết</button>
              </div>
              <ul className="picked-list">
                {listed.map((f, i) => (
                  <li key={`${f.webkitRelativePath || f.name}-${i}`} className={isAllowed(f) ? "" : "bad"}>
                    <span className="picked-name" title={f.webkitRelativePath || f.name}>
                      {f.webkitRelativePath || f.name}
                    </span>
                    <span className="picked-size">{isAllowed(f) ? formatSize(f.size) : "Không hỗ trợ"}</span>
                    {!isFolder && (
                      <button className="mini-btn" onClick={() => removeAt(i)} aria-label={`Bỏ ${f.name}`}>
                        <IcoX size={14} />
                      </button>
                    )}
                  </li>
                ))}
                {files.length > MAX_LISTED && (
                  <li className="more">… và {files.length - MAX_LISTED} file khác</li>
                )}
              </ul>
            </div>
          )}

          {invalid > 0 && (
            <div className="callout danger">
              <IcoAlert size={16} />
              <span>
                Có {invalid} file không thuộc loại được hỗ trợ.{" "}
                {isFolder ? "Hãy bỏ chúng khỏi thư mục rồi chọn lại." : "Hãy bỏ các file đó trước khi tải lên."}
              </span>
            </div>
          )}

          {note && (
            <div className="callout info">
              <IcoInfo size={16} />
              <span className="pre-line">{note}</span>
            </div>
          )}
        </div>

        <div className="modal-foot">
          <button className="btn btn-quiet" onClick={onCancel}>Hủy</button>
          <button className="btn btn-primary" onClick={() => onSubmit(files)} disabled={files.length === 0}>
            {isFolder ? <IcoFolderUp size={17} /> : <IcoCloudUp size={17} />}
            <span>Tải lên{files.length > 0 ? ` (${files.length})` : ""}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
